import { DeviceType, HALInterface, HALFrameType, IHALInterface, IHALDevice, ISimulatorDevice } from "./Base";
import { SwitchInterface, IPStack, IPMode, IPConfig, BLANK_IP, RouterDevice, SwitchDevice, DHCPServerApplication } from "./Ethernet";
import { generateRandomMac } from '../../../tools';
import { Node } from "@antv/x6";

interface WIFIInfo {
    SSID: string;
    encryptMode: string;
    channel: string;
    strength: string;
}

interface WIFIScanRecord extends WIFIInfo {
    connected: boolean
}

export interface WIFISetting extends WIFIInfo {
    password: string;
}

// WIFI设备
export interface IWIFIDevice {
    connect(SSID: string, password: string): Promise<void>
    getHAL(): IHALDevice
    restore(): void
    disconnect(): void
}

// WIFI环境
//   提供扫描函数，返回AP列表：SSID、信号强度、加密方式
//   提供连接函数，在AP侧和客户端各生成一个虚拟以太网接口，将其连接
//   当AP删除 或 客户端删除时，对应虚拟以太网接口被删除，则连接中断
class WIFIEnviroment {
    protected aps: Array<APDevice> = [];

    // 添加AP设备
    addAP(ap: APDevice) {
        this.aps.push(ap);
    }

    // 卸载AP设备
    removeAP(ap: APDevice) {
        this.aps.splice(this.aps.indexOf(ap), 1);
    }

    // 扫描函数，返回AP列表
    scan(): Array<APDevice> {
        return this.aps;
    }
}

const wifiEnv = new WIFIEnviroment();

// AP
export class APDevice implements ISimulatorDevice, IHALDevice {
    protected node: Node;
    protected bridge: any;
    protected iPort: HALInterface
    protected airPorts: Array<HALInterface>
    protected airClients: Array<IWIFIDevice>
    protected wifiSetting: WIFISetting;

    constructor(node: Node) {
        this.node = node;
        this.iPort = new SwitchInterface(this, generateRandomMac());
        this.airPorts = [];
        this.airClients = [];
        this.wifiSetting = {
            SSID: 'yopu',
            encryptMode: 'none',
            password: '',
            channel: '0',
            strength: '标准'
        };

        // 将自己加入WIFI列表
        wifiEnv.addAP(this);
    }

    getNode(): Node<Node.Properties> {
        return this.node;
    }

    get wifiInfo(): WIFIInfo {
        return this.wifiSetting;
    }

    // 断开全部无线设备
    disconnectAllAirClient() {
        const { airClients } = this;
        for (const airClient of airClients) {
            airClient.disconnect();
        }

        this.airClients = [];
    }

    updateWIFISetting(setting: WIFISetting): void {
        this.wifiSetting = setting;

        // 更新WIFI设定会造成全部已经连接设备全部断开
        this.disconnectAllAirClient();

        this.airPorts = []

        // AP嵌入在无线路由器内部的时候，无线路由器报送onChange就好了
        if (this.bridge) {
            this.bridge.onChange();
        }
    }

    auth(password: string): boolean {
        // 如果是非加密模式
        if (this.wifiSetting.encryptMode === 'none') {
            return true;
        }

        // 加密模式，验证SSID和密码是否一致
        return (this.wifiSetting.password === password);
    }

    // 连接函数，在AP侧和客户端各生成一个虚拟以太网接口，将其连接
    connect(client: IWIFIDevice, password: string): IHALInterface {
        // 验证SSID，密码是否一致，不一致报错退出
        if (!this.auth(password)) {
            throw new Error(`无法通过安全验证，请检查密码是否一致`);
        }

        // 创建AP网卡和客户端网卡
        const clientAirPort = new HALInterface(client.getHAL(), generateRandomMac());
        const apAirPort = new SwitchInterface(this, generateRandomMac());
        this.airPorts.push(apAirPort);
        this.airClients.push(client);

        // 关联网卡
        apAirPort.link(clientAirPort);
        clientAirPort.link(apAirPort);

        // 返回客户端网卡
        return clientAirPort;
    }

    // 恢复连接函数
    restore(client: IWIFIDevice, clientAirPort: IHALInterface) {
        // 获取原有网卡对端MAC
        const connectedMac = clientAirPort.connectedMac;
        if (!connectedMac) {
            throw new Error('无法找到对端AP空口');
        }

        // 恢复连接
        const apAirPort = new SwitchInterface(this, connectedMac);
        this.airPorts.push(apAirPort);

        // 关联网卡
        apAirPort.link(clientAirPort);
        clientAirPort.link(apAirPort);

        // 关联设备
        this.airClients.push(client);
    }

    // 断开连接
    disconnect(client: IWIFIDevice, clientAirPort: IHALInterface) {
        // 获取网卡
        const pairedAirPort = clientAirPort.connectedInterface;
        if (pairedAirPort) {
            const pairedAirPortIndex = this.airPorts.findIndex(airPort => airPort === pairedAirPort);
            if (-1 === pairedAirPortIndex) {
                throw new Error('没有找到配对的空口');
            }

            // 解除网卡关联
            pairedAirPort.unlink();
            clientAirPort.unlink();

            // 删除网卡
            this.airPorts.splice(pairedAirPortIndex, 1);
        }

        // 删除设备
        const clientIndex = this.airClients.indexOf(client);
        if (-1 === clientIndex) {
            throw new Error('没有找到配对的设备');
        }

        this.airClients.splice(clientIndex, 1);
    }

    // 配置桥接器，用于变更通知
    setBridge(bridge: any): void {
        this.bridge = bridge;
    }

    get deviceType() {
        return DeviceType.AP;
    }

    // 卸载设备
    destory(): void {
        // 断开有线连接
        const { iPort } = this;
        iPort.unlink();

        // 断开全部无线连接
        this.disconnectAllAirClient();

        wifiEnv.removeAP(this);
    }

    // 此处只返回有线连接网口
    getPorts(): Array<IHALInterface> {
        return [this.iPort];
    }

    // 转换为JSON
    toJSON() {
        const { iPort, wifiSetting } = this;

        return {
            iPort: iPort.toJSON(),
            wifiSetting: wifiSetting,
            deviceType: DeviceType.AP,
        };
    }

    // 自JSON加载
    fromJSON(config: any): void {
        // 恢复端口配置
        const { iPort } = this;
        iPort.fromJSON(config.iPort);

        // 恢复WIFI设置
        this.wifiSetting = config.wifiSetting;
    }

    /**
     * 端口接收到请求
     * @param fromMAC 
     * @param toMAC 
     * @param type 
     * @param data 
     * @param callback 
     */
    HAL_Recv(fromInterface: IHALInterface, fromMAC: string, toMAC: string, type: HALFrameType, data: any, callback: Function): void {
        const { iPort, airPorts } = this;
        const ports = [iPort, ...airPorts];
        for (const port of ports) {
            // 没有连接电路端口，跳过
            if (!port.connected) {
                continue;
            }

            // 判断是否为发出的电缆，跳过
            if (port === fromInterface) {
                continue;
            }

            // 转发消息
            port.HAL_Send_Transparent(fromMAC, toMAC, type, data, callback);
        }
    }
}


// 移动终端
export class MobileDevice extends IPStack implements ISimulatorDevice, IWIFIDevice {
    protected node: Node;
    protected bridge: any;
    protected connectedAP: APDevice | null = null;
    protected connectedAPSSID: string | null = null;

    constructor(node: Node, ipConfig = {
        ipMode: IPMode.DHCP, // IP模式
        ipAddress: BLANK_IP, // IP地址
        subnetMask: '255.255.255.0', // 子网掩码
        gateway: BLANK_IP, // 网关
        dns: BLANK_IP, // 域名服务器
    }) {
        super(ipConfig);

        this.node = node;
    }

    getNode(): Node<Node.Properties> {
        return this.node;
    }

    getHAL(): IHALDevice {
        return this.networkCard;
    }

    scan(): Array<WIFIScanRecord> {
        const SSIDSet = new Set();

        // SSID去重
        return wifiEnv.scan().filter((ap) => {
            const SSID = ap.wifiInfo.SSID;
            if (SSIDSet.has(SSID)) {
                return false;
            }

            SSIDSet.add(SSID);
            return true;
        }).map((ap) => {
            return {
                ...ap.wifiInfo,
                connected: this.connectedAP === ap
            };
        });
    }

    getPorts() {
        return [];
    }

    async destory(): Promise<void> {
        this.disconnect();
    }

    disconnect() {
        const { connectedAP } = this;
        if (!connectedAP) {
            return;
        }

        // 断开与AP连接
        connectedAP.disconnect(this, this.networkCard.getPort());

        // 释放IP地址信息
        this.updateIPConfig({
            ipMode: IPMode.DHCP, // IP模式
            ipAddress: BLANK_IP, // IP地址
            subnetMask: '255.255.255.0', // 子网掩码
            gateway: BLANK_IP, // 网关
            dns: BLANK_IP, // 域名服务器
        });

        this.connectedAP = null;
        this.connectedAPSSID = null;
    }

    async connect(SSID: string, password: string): Promise<void> {
        // 如果已经连接，那么断开连接
        this.disconnect();

        const ap = wifiEnv.scan().find(ap => ap.wifiInfo.SSID === SSID);
        if (!ap) {
            throw new Error(`没有找到SSID为${SSID}的AP`);
        }

        // 获取绑定上的网卡
        const clientInterface = ap.connect(this, password);

        // 替换原有网卡
        this.networkCard.setPort(clientInterface);

        // 保存连接信息
        this.connectedAP = ap;
        this.connectedAPSSID = SSID;

        // 若为DHCP模式则通过DHCP获取地址
        if (this.ipConfig.ipMode === 'DHCP') {
            try {
                await this.DHCP_discover();
            }
            catch (e) {
                this.disconnect();
                throw e;
            }
        }

        this.bridge.onChange();
    }

    get deviceType() {
        return DeviceType.MobileDevice;
    }

    // 配置桥接器，用于变更通知
    setBridge(bridge: any): void {
        this.bridge = bridge;
    }

    // PPPOE拨号成功或者DHCP客户端获得IP后更新配置信息
    updateIPConfig(ipConfig: IPConfig): void {
        super.updateIPConfig(ipConfig);
        this.bridge.onChange();
    }

    toJSON() {
        return {
            ...super.toJSON(),
            connectedSSID: this.connectedAPSSID,
            deviceType: DeviceType.MobileDevice,
        };
    }

    fromJSON(config: any): void {
        super.fromJSON(config);
        this.connectedAPSSID = config.connectedSSID;
    }

    // 恢复连接
    restore() {
        if (!this.connectedAPSSID) {
            return;
        }

        // 考虑如果有connectedSSID，是否在这里自动重连，前提是AP必须在移动终端之前完成初始化工作
        const ap = wifiEnv.scan().find(ap => ap.wifiInfo.SSID === this.connectedAPSSID);
        if (!ap) {
            throw new Error(`没有找到SSID为${this.connectedAPSSID}的AP`);
        }

        this.connectedAP = ap;

        // 获取原有网卡
        const clientInterface = this.networkCard.getPort();

        // 获取绑定上的网卡
        ap.restore(this, clientInterface);
    }

    async DHCP_discover(): Promise<IPConfig> {
        const ipConfig = await super.DHCP_discover();
        return Promise.resolve(ipConfig);
    }
}

// 无线路由器：家庭网关
export class HomeGatewayDevice extends RouterDevice {
    // 继承路由器

    // 交换机/集线器
    protected hub: SwitchDevice;

    // AP
    protected ap: APDevice;

    // DHCP服务器应用
    protected dhcpServerApp: DHCPServerApplication;

    // LAN侧端口
    protected lanPorts: IHALInterface[];

    // 内部交换机端口
    protected switchLanPort: IHALInterface;
    protected switchAPLanPort: IHALInterface;

    // 路由器LAN口
    protected routerLanPort: IHALInterface;

    // AP LAN口
    protected APLanPort: IHALInterface;

    constructor(node: Node) {
        super(node);

        // 初始化设备
        // 4 x LAN口 + AP口 + DHCP服务器口 + 路由器LAN口 = 7口
        this.hub = new SwitchDevice(node, 6);
        this.ap = new APDevice(node);

        // 输出LAN口
        const lanPorts = this.hub.getPorts().concat();

        // 最右侧两个端口内部使用
        this.switchLanPort = lanPorts.pop() as IHALInterface;
        this.switchAPLanPort = lanPorts.pop() as IHALInterface;

        // 剩余左侧4个端口作为LAN口使用
        this.lanPorts = lanPorts;

        // 交换机与路由器LAN口接线
        this.routerLanPort = this.getLan().getPorts()[0];
        this.switchLanPort.link(this.routerLanPort);
        this.routerLanPort.link(this.switchLanPort);

        // 交换机与AP接线
        this.APLanPort = this.ap.getPorts()[0];
        this.switchAPLanPort.link(this.APLanPort);
        this.APLanPort.link(this.switchAPLanPort);

        // 在LAN口上初始化DHCP服务器应用
        this.dhcpServerApp = new DHCPServerApplication(false);
        this.lan.install(this.dhcpServerApp);
    }

    // 获取对外的端口，1个WAN口，4个LAN口
    getPorts(): IHALInterface[] {
        return this.lanPorts.concat(this.wan.getPorts());
    }

    destory() {
        // 卸载应用
        this.lan.uninstall(this.dhcpServerApp);

        // 取消端口连接
        this.switchLanPort.unlink();
        this.switchAPLanPort.unlink();
        this.routerLanPort.unlink();
        this.APLanPort.unlink();

        // 销毁AP
        this.ap.destory();

        // 销毁交换机
        this.hub.destory();

        // 销毁路由
        super.destory();
    }

    toJSON() {
        return {
            // 路由配置
            ...super.toJSON(),

            // AP配置
            ap: this.ap.toJSON(),

            // 交换机配置
            hub: this.hub.toJSON(),

            // DHCP应用配置
            dhcp: this.dhcpServerApp.toJSON(),

            // 设备类型家庭网关
            deviceType: DeviceType.HomeGateway,
        }
    }

    fromJSON(config: any): void {
        const { hub, ap, dhcp, ...router } = config;

        // 路由配置
        super.fromJSON(router);

        // 交换机配置
        this.hub.fromJSON(hub);

        // AP配置
        this.ap.fromJSON(ap);

        // DHCP服务配置
        this.dhcpServerApp.fromJSON(dhcp);
    }

    getAP(): APDevice {
        return this.ap;
    }

    getDHCP(): DHCPServerApplication {
        return this.dhcpServerApp;
    }

    get deviceType() {
        return DeviceType.HomeGateway;
    }
}
